/**
 * TimezoneSetupBanner — persistent nudge for tenants that never set a company
 * timezone. Without an explicit zone the whole app (including scheduled
 * meetings) silently formats in the America/New_York default, so times look
 * wrong for everyone off Eastern. Shown only to users who can actually fix it
 * (edit_company_settings; owners pass via can()). Auto-hides once a valid zone
 * is saved — `user.company_timezone_set` flips true after refreshUser().
 */
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';

export default function TimezoneSetupBanner() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();

  // Only when the flag is EXPLICITLY false (present in the payload) — never for
  // older sessions where the field is absent, and never once it's set.
  if (!user || user.company_timezone_set !== false) return null;
  if (!can('edit_company_settings')) return null;

  return (
    <div
      data-testid="timezone-setup-banner"
      className="flex items-center gap-3 px-4 py-2 border-b text-xs bg-[#3A2A12] border-amber-700/40 text-amber-100"
    >
      <Clock className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong className="font-semibold">Set your company time zone.</strong>{' '}
        <span className="opacity-90">
          Until you do, meetings and every time in the app show in Eastern (New York) by default —
          which is likely wrong for your team. Setting it corrects your scheduled meetings automatically.
        </span>
      </div>
      <button
        onClick={() => navigate('/settings?tab=company')}
        data-testid="timezone-setup-banner-btn"
        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-medium bg-white/10 hover:bg-white/20 border border-white/20"
      >
        Set time zone
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
